import React, {useRef, useEffect, useState} from 'react';
import {Point} from '../../../../data/models/LessonContent/ContentFreeform';
import '../styles.css';
import {wrapLabel} from '../../../../utils/wrapText';
import {BASELINE_SVG_CANVAS_WIDTH} from '../SvgCanvas';

interface Props {
  position: Point;
  text: string;
  color: string;
  updateTextOptions?: (position: Point) => void;
  onClick?: () => void;
  selected?: boolean;
  width: number;
}

export const DEFAULT_SVG_FONT_SIZE = 7.5;
const BORDER_RADIUS = 10;
const MAX_TEXT_WIDTH = 180;
const TEXT_OFFSET = 5;
const BOX_HEIGHT = 52;
const BOX_WIDTH = 220;

const EditorSvgText = React.memo(
  ({position, text, updateTextOptions, onClick, selected, width}: Props) => {
    const [textPosition, setTextPosition] = useState<Point>({x: 0, y: 0});
    const [resizeData, setResizeData] = useState({
      active: false,
      offset: {x: 0, y: 0},
      initial: {x: 0, y: 0},
    });
    const [textDimensions, setTextDimensions] = useState({width: 0, height: 0});
    const textRef = useRef<SVGTextElement | null>(null);

    useEffect(() => {
      setTextPosition({...position});
    }, [position]);

    useEffect(() => {
      if (textRef.current) {
        const bbox = textRef.current.getBBox();
        setTextDimensions({
          width: bbox.width,
          height: bbox.height,
        });
      }
    }, [text, textRef, textRef.current]);

    const getScaleFactor = () => {
      const scale = (width * 100) / BASELINE_SVG_CANVAS_WIDTH;
      return scale / 100;
    };

    const handlePointerDown = (e: any) => {
      if (!updateTextOptions) return;
      const el = e.target;
      const x = e.clientX;
      const y = e.clientY;
      el.setPointerCapture(e.pointerId);
      setResizeData({
        ...resizeData,
        active: true,
        offset: {
          x,
          y,
        },
        initial: {
          x: textPosition.x,
          y: textPosition.y,
        },
      });
    };

    const handleItemClick = (e: any) => {
      if (onClick) {
        onClick();
        e.stopPropagation();
      }
    };

    const handlePointerMove = (e: any) => {
      const x = e.clientX;
      const y = e.clientY;
      if (resizeData.active) {
        const offsetX = (resizeData.offset.x - x) / getScaleFactor();
        const offsetY = (resizeData.offset.y - y) / getScaleFactor();
        setTextPosition({
          x: resizeData.initial.x - offsetX,
          y: resizeData.initial.y - offsetY,
        });
      }
    };

    const handlePointerUp = () => {
      setResizeData({
        ...resizeData,
        active: false,
      });
      if (updateTextOptions) {
        updateTextOptions(textPosition);
      }
    };

    const getTextChunks = () => {
      return wrapLabel(text, MAX_TEXT_WIDTH, DEFAULT_SVG_FONT_SIZE);
    };

    const getTextY = () => {
      return (BOX_HEIGHT - textDimensions.height) / 2 + TEXT_OFFSET;
    };

    return (
      <g
        transform={`translate(${textPosition.x}, ${textPosition.y})`}
        onClick={handleItemClick}>
        <rect
          x={0}
          y={0}
          width={BOX_WIDTH}
          height={BOX_HEIGHT}
          stroke={selected ? '#53B483' : 'rgba(0, 0, 0, 0.10)'}
          strokeWidth={selected ? 2 : 1}
          fill="rgba(0, 0, 0, 0.68)"
          cursor={updateTextOptions ? 'move' : onClick ? 'pointer' : ''}
          rx={BORDER_RADIUS}
          ry={BORDER_RADIUS}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerMove={handlePointerMove}
        />
        <text
          ref={textRef}
          x={0}
          y={getTextY()}
          className="SvgText"
          fill={'#ffffff'}
          fontSize={DEFAULT_SVG_FONT_SIZE}
          dominantBaseline="middle"
          textAnchor="start"
          fontWeight="500"
          cursor={updateTextOptions ? 'move' : onClick ? 'pointer' : ''}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerMove={handlePointerMove}>
          {getTextChunks().map((t, i) => (
            <tspan
              fontSize={DEFAULT_SVG_FONT_SIZE}
              key={i}
              x={30}
              dy={i === 0 ? 0 : DEFAULT_SVG_FONT_SIZE}>
              {t}
            </tspan>
          ))}
        </text>
        <g transform={`translate(${10}, ${BOX_HEIGHT / 2 - 8})`}>
          <circle cx="8" cy="8" r="8" fill="#011E1E" fillOpacity="1" />
          <path
            d="M11.334 10L20.6673 16L11.334 22V10Z"
            fill="#B8EF36"
            stroke="#B8EF36"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            transform="scale(0.5)"
          />
        </g>
      </g>
    );
  },
);

export default EditorSvgText;
